/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useQuery } from '@apollo/client'
import { Separator } from '@farol-ds/react'
import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  buildExperimentContext,
  buildMetricsProperties,
  DocPopoverElement,
  DocSearch,
  DocSearchElement,
  Docview,
  ExperimentsProvider,
  getDocumentStatusCode,
  isGoogleBotAgent,
  JUSBRASIL_HOTJAR_SITE_ID,
  MetricsProperties,
  printStyles,
  RecommendedDocuments,
  redirectDocumentUrl,
  shouldConsumeViewCredits,
  useConsumeCredits,
  useDwellTime,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { ABTestExperiment } from '@jusbrasil-web/shared-ab-test'
import { SetNonNullableQueryRootProps } from '@jusbrasil-web/shared-apollo-client/utilities'
import { AppBar } from '@jusbrasil-web/shared-appbar'
import { COMPONENT_CLASS, FEEDBACK_NAME } from '@jusbrasil-web/shared-ga4-events'
import { useViewportSize } from '@jusbrasil-web/shared-hooks'
import { hotjarEvent, HotjarScript } from '@jusbrasil-web/shared-hotjar-script'
import { ChatWeb, SDQuiz, SdQuizStickyCta } from '@jusbrasil-web/shared-seus-direitos'
import {
  type ResolvedArticleQuiz,
  getArticleQuiz,
} from '@jusbrasil-web/shared-seus-direitos/article-quizzes'
import { getArticleSummaryItems } from '@jusbrasil-web/shared-seus-direitos/article-summaries'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import UAParser from 'ua-parser-js'

import {
  AccordionComments,
  ArticleNewsDocument,
  ArticleNewsMetaTags,
  ArticleNewsToolbar,
  Comments,
  ModerationBanner,
  PublicationAppbar,
  PublicationHeader,
  PublicationSidebar,
} from '../../components'
import { OrientationBox } from '../../components/orientation-box'
import {
  getActiveExperiments,
  useSdChatWebDocviewExperiment,
  useSdDocviewChatsTracking,
} from '../../experiment'
import { SegmentationProvider } from '../../hooks/segmentation-hook'
import { useDisableGoogleOneTap } from '../../hooks/useDisableGoogleOneTap'
import { extractFromUrl } from '../../utils'
import { getUserAgentProperties } from '../../utils/user-agent'
import styles from '../publications.module.scss'
import { ArticleRootQuery, ArticleRootQueryVariables } from './__generated__/query.graphql'
import { ArticleRoot } from './query'

export type ArticlePageProps = {
  variables: ArticleRootQueryVariables
  metricsProperties: MetricsProperties
  experimentContext?: ABTestExperiment[]
  summaryItems?: string[] | null
  quiz?: ResolvedArticleQuiz | null
  disableTouchFeatures?: boolean
  httpError?: true
  shouldConsumeViewCredits?: boolean
  isGoogleBotAgent?: boolean
}

type ArticleRootQueryPage = SetNonNullableQueryRootProps<ArticleRootQuery, 'document'>

export function ArticlePage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <ExperimentsProvider experiments={props.experimentContext}>
      <ArticlePageContent {...props} />
    </ExperimentsProvider>
  )
}

function ArticlePageContent(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const {
    variables,
    metricsProperties,
    summaryItems = null,
    quiz = null,
    disableTouchFeatures,
    httpError,
    shouldConsumeViewCredits,
    isGoogleBotAgent,
  } = props

  const contentRef = useRef<HTMLDivElement>(null)
  const docSearchRef = useRef<DocSearchElement>(null)
  const docPopoverRef = useRef<DocPopoverElement>(null)
  const sdQuizRef = useRef<HTMLDivElement>(null)
  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()

  const { data } = useQuery<ArticleRootQueryPage>(ArticleRoot, { variables })
  const { document, viewer = null } = data?.root || {}
  const hasPaidPlan = !!viewer?.hasPaidPlan

  const userAgentProps = useMemo(() => getUserAgentProperties(), [])

  const isSeusDireitosSegment =
    document?.publicationSegmentation?.typeSegmentation === 'SEUS_DIREITOS'

  const isExperimentArticle = summaryItems !== null || quiz !== null

  const { isChatWebEligible, isQuizEligible, isSdChatExperimentOn } = useSdChatWebDocviewExperiment(
    { summaryItems, quiz }
  )
  const { experiment, variant } = useSdDocviewChatsTracking({ isExperimentArticle })
  const { width: viewportWidth } = useViewportSize()
  const headerCtaViewSentRef = useRef(false)

  useDisableGoogleOneTap(!!viewer)
  useConsumeCredits(shouldConsumeViewCredits, document)
  useDwellTime(document, sendMetrics, metricsProperties)

  // This hook should be run only once per page, that's why docId should be its only dependency
  useEffect(() => {
    sendMetrics('Doc.Viewed', metricsProperties)

    hotjarEvent('event', 'ExperimentArticleFarolRollout')
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [document?.docId])

  const buildCtaViewMetadata = useCallback(
    (barIsSticky: boolean) => {
      return {
        copy: 'Receba uma orientação',
        page: 'docview-artigo',
        action: 'open_chat',
        position: barIsSticky ? 'anchor' : 'header',
        doc_id: metricsProperties.doc_id,
        experiment,
        variant,
      }
    },
    [metricsProperties.doc_id, experiment, variant]
  )

  useEffect(() => {
    if (headerCtaViewSentRef.current) return
    if (viewportWidth === 0) return
    if (
      document?.publicationSegmentation?.typeSegmentation === 'SEUS_DIREITOS' &&
      !isSdChatExperimentOn
    ) {
      const metadata = buildCtaViewMetadata(false)

      sendMetrics('SeusDireitos.CtaView', {
        app_id: 'web-docview',
        page: metadata.page,
        browser: userAgentProps.browser,
        os: userAgentProps.os,
        user_agent: userAgentProps.user_agent,
        dvce_type: userAgentProps.dvce_type,
        dvce_ismobile: userAgentProps.dvce_ismobile,
        device_name: userAgentProps.device_name,
        metadata,
      })
      headerCtaViewSentRef.current = true
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    document?.docId,
    document?.publicationSegmentation?.typeSegmentation,
    buildCtaViewMetadata,
    userAgentProps,
    isSdChatExperimentOn,
    viewportWidth,
  ])

  const onToolbarStickyChange = useCallback(
    (barIsSticky: boolean) => {
      if (
        document?.publicationSegmentation?.typeSegmentation === 'SEUS_DIREITOS' &&
        !isSdChatExperimentOn
      ) {
        const metadata = buildCtaViewMetadata(barIsSticky)

        if (barIsSticky) {
          sendMetrics('SeusDireitos.CtaView', {
            app_id: 'web-docview',
            page: metadata.page,
            browser: userAgentProps.browser,
            os: userAgentProps.os,
            user_agent: userAgentProps.user_agent,
            dvce_type: userAgentProps.dvce_type,
            dvce_ismobile: userAgentProps.dvce_ismobile,
            device_name: userAgentProps.device_name,
            metadata,
          })
        }
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      document?.publicationSegmentation?.typeSegmentation,
      buildCtaViewMetadata,
      userAgentProps,
      isSdChatExperimentOn,
    ]
  )

  const headerToolbar = useMemo(
    () =>
      document ? (
        <ArticleNewsToolbar
          document={document}
          viewer={viewer}
          docSearchRef={docSearchRef}
          ga4EventData={{
            componentClass: COMPONENT_CLASS.DOCUMENT_HEADER,
            resourceId: document.boxResourceId,
            feedbackName: FEEDBACK_NAME.ARTICLE_CONTENT_FEEDBACK,
          }}
          sendMetrics={sendMetrics}
          fullCta={false}
          isExperimentArticle={isExperimentArticle}
        />
      ) : null,
    [document, viewer, sendMetrics, isExperimentArticle]
  )

  const appBarToolbar = useMemo(
    () =>
      document ? (
        <ArticleNewsToolbar
          document={document}
          viewer={viewer}
          docSearchRef={docSearchRef}
          ga4EventData={{
            componentClass: COMPONENT_CLASS.DOCUMENT_TOPBAR,
            resourceId: document.boxResourceId,
            feedbackName: FEEDBACK_NAME.ARTICLE_CONTENT_FEEDBACK,
          }}
          sendMetrics={sendMetrics}
          fullCta
          onToolbarStickyChange={onToolbarStickyChange}
          isExperimentArticle={isExperimentArticle}
        />
      ) : null,
    [document, viewer, sendMetrics, onToolbarStickyChange, isExperimentArticle]
  )

  if (httpError || !document) return null

  const handleCopy = () => {
    sendCopyEvents(metricsProperties)
  }

  const onLoadMoreComments = () => docPopoverRef.current?.refreshElementNodes()

  const isSegmentedPublication = !!document.publicationSegmentation

  return (
    <AppBar.Provider>
      <SegmentationProvider segmentationData={document.publicationSegmentation}>
        <Docview
          document={document}
          viewer={viewer}
          handleCopy={handleCopy}
          metricsProperties={metricsProperties}
          canCopyContent={true}
          dispatcherRef={dispatcherRef}
          contentRef={dispatcherRef}
          docPopoverRef={docPopoverRef}
          disableTouchFeatures={!!disableTouchFeatures}
          topbarCurrentNavKey="ARTIGOS"
          isSegmentedPublication={isSegmentedPublication}
          metaTags={<ArticleNewsMetaTags document={document} />}
          afterTopbar={
            <>
              {hasPaidPlan && (
                <DocSearch
                  contentRef={contentRef}
                  ref={docSearchRef}
                  slug={document.slug || undefined}
                />
              )}
            </>
          }
          header={
            <>
              <ModerationBanner document={document} viewer={viewer} />
              <PublicationHeader
                document={document}
                viewer={viewer}
                toolbar={headerToolbar}
                hideToolbar={isSdChatExperimentOn}
              />
              {isSegmentedPublication && !isSdChatExperimentOn && (
                <Separator className={styles.segmentedArticleSeparator} />
              )}
              <AppBar.StickyTrigger />
            </>
          }
          asideContent={<PublicationSidebar document={document} />}
          mainContent={
            <>
              {isChatWebEligible && summaryItems && (
                <div className={styles.chatWebWrapper}>
                  <ChatWeb
                    items={summaryItems}
                    docId={document?.docId}
                    dvceType={userAgentProps.dvce_type}
                  />
                </div>
              )}
              {isQuizEligible && quiz && (
                <>
                  <div ref={sdQuizRef} className={styles.chatWebWrapper}>
                    <SDQuiz {...quiz} docId={document?.docId} dvceType={userAgentProps.dvce_type} />
                  </div>
                  <SdQuizStickyCta
                    quizRef={sdQuizRef}
                    docId={document?.docId}
                    dvceType={userAgentProps.dvce_type}
                  />
                </>
              )}
              <ArticleNewsDocument
                document={document}
                contentRef={contentRef}
                isExperimentArticle={isExperimentArticle}
                docId={document?.docId}
              />
            </>
          }
          relatedContent={
            <>
              <RecommendedDocuments document={document} />

              {document.recommendations.length > 0 && (
                <Separator
                  className={classNames(
                    printStyles.unprintable,
                    styles.recommendedDocumentsSeparator
                  )}
                />
              )}

              {isSeusDireitosSegment && (
                <OrientationBox isExperimentArticle={isExperimentArticle} />
              )}

              {isSeusDireitosSegment && (
                <Separator
                  className={classNames(
                    printStyles.unprintable,
                    styles.recommendedDocumentsSeparator,
                    styles.orientationBoxSeparator
                  )}
                />
              )}

              {isSeusDireitosSegment ? (
                <AccordionComments
                  document={document}
                  viewer={viewer}
                  onLoadMore={onLoadMoreComments}
                  className={printStyles.unprintable}
                />
              ) : (
                <Comments
                  document={document}
                  viewer={viewer}
                  onLoadMore={onLoadMoreComments}
                  className={printStyles.unprintable}
                />
              )}
            </>
          }
          endContent={
            <PublicationAppbar
              document={document}
              className={classNames(
                printStyles.unprintable,
                isSdChatExperimentOn && styles.hideAppbarEndOnMobile
              )}
              toolbar={appBarToolbar}
            />
          }
        />
        <HotjarScript hjid={JUSBRASIL_HOTJAR_SITE_ID} />
      </SegmentationProvider>
    </AppBar.Provider>
  )
}

const getServerSideProps: GetServerSideProps<ArticlePageProps> = async (ctx) => {
  const { parts } = ctx.query
  if (!parts) {
    return {
      notFound: true,
    }
  }
  const { docId } = extractFromUrl(Array.isArray(parts) ? parts : [parts])
  if (!docId) {
    return {
      notFound: true,
    }
  }

  const variables: ArticleRootQueryVariables = {
    docId,
    isNewsPage: false,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { data } = await client.query<ArticleRootQuery>({
    query: ArticleRoot,
    variables,
  })
  const { document, viewer } = data?.root ?? {}
  if (!document) {
    return {
      notFound: true,
    }
  }

  const experimentContext = await buildExperimentContext(ctx, getActiveExperiments())
  const metricsProperties = buildMetricsProperties(ctx, document, viewer, experimentContext)
  const summaryItems = getArticleSummaryItems(document.docId)
  const quiz = getArticleQuiz({ docId: String(document.docId) })

  const status = getDocumentStatusCode(ctx.req, document, viewer)
  if (status !== 200) {
    ctx.res.statusCode = status
    return {
      props: {
        variables,
        metricsProperties,
        experimentContext,
        httpError: true,
      },
    }
  }

  const redirect = document.url && redirectDocumentUrl(ctx, document.url)
  if (redirect) {
    return { redirect }
  }

  const ua = UAParser(ctx.req.headers['user-agent'])
  const disableTouchFeatures = ua.device.type === 'mobile' || ua.device.type === 'tablet'

  return {
    props: {
      ...withApolloProps(),
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      variables,
      metricsProperties,
      experimentContext,
      summaryItems,
      quiz,
      disableTouchFeatures,
      shouldConsumeViewCredits: shouldConsumeViewCredits(ctx.req, viewer),
      isGooglebotAgent: isGoogleBotAgent(ctx.req),
    },
  }
}

export const ArticlePageExports = { getServerSideProps }
